/* eslint-disable indent */
import { useEffect, useRef, useState } from "react"
import Screen from "src/shared/components/layout/screen/Screen"
import Resumen from "./sections/Resumen/Resumen"
import { useNavigate, useParams } from "react-router-dom"
import LabelTitle from "./components/LabelTitle/LabelTitle"
import {
    Reserva,
    Tarifa,
    TiposAlojamientos,
    TiposEntradas,
    TiposPagos,
    useGetHabitacionQuery,
    useGetColaboradoresQuery,
    EstadosTurnosAtencion,
} from "src/gql/schema"
import { v4 as uuid } from "uuid"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useProfile } from "src/shared/hooks/useProfile"
import { scheduleTypes } from "./VentaHabitacion.constants"
import { PAYMENT_METHODS, PAYMENT_TYPES } from "src/constants/payments"
import { useDispatch, useSelector } from "react-redux"
import { getPropinaList } from "src/shared/sections/payment/helpers/propinas"
import {
    selectTarifa,
    setTarifas,
    toggleModalPagoMixtoEdited,
    toggleModalPagoMixtoOpen,
} from "src/store/ventaHabitacion/ventaHabitacionSlice"
import { RootState } from "src/store/store"
import useSetCosts from "./hooks/useSetCosts"
import useResetPayments from "./hooks/useResetPayments"
import useResetForm from "./hooks/useResetForm"
import { useDate } from "src/shared/hooks/useDate"
import useSnackbar from "src/shared/hooks/useSnackbar"
import { toggleRoomDetailsDrawer } from "src/store/navigation/navigationSlice"
import useReservaPayments from "./hooks/useReservaPayments"
import useSetFormStateFromReserva from "./hooks/useSetFormStateFromReserva"
import { addCurrentTime } from "src/shared/helpers/addCurrentTime"
import { sum, times } from "src/shared/helpers/calculator"
import useShowPago from "./hooks/useShowPago"
import useShowPagoReserva from "./hooks/useShowPagoReserva"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import useValidSameDayRent from "./hooks/useValidSameDayRent"
import { ventaHabitacionDefaultValues } from "./helpers/genDefaultValues"
import LoaderComponent from "src/shared/components/layout/loader/Loader"
import useEscapeKey from "src/shared/hooks/useEscapeKey"
import useModalMatricula from "./hooks/useModalMatricula"
import { InputTextSuggestion } from "src/shared/components/forms/input-text-suggestions/InputTextSuggestions"
import { isValidPropina } from "./helpers/isVisibleCardNumber"
import { DetallePago } from "src/shared/sections/payment/types/pagos"
import useIsLoadingData from "./hooks/useIsLoadingData"
import { LovePoint } from "src/pages/easyrewards/components/InputAbonarEasyRewards/InputAbonarEasyRewards.types"

import ModalLovePointsError from "../easyrewards/components/ModalLovePointsError/ModalLovePointsError"
import ModalPagoMixtoLovePoints from "../easyrewards/components/ModalPagoMixtoLovePoints/ModalPagoMixtoLovePoints"
import { useTarifasValidation } from "../reservaciones/registro-reservas/hooks/useTarifasValidation"
import useAuthOnTarifaNocheSelect from "./hooks/useAuthOnTarifaNocheSelect"
import useAuthOnCortesiaSelect from "../../shared/hooks/useAuthOnCortesiaSelect"
import useAuthOnCortesiaFromMixtoSelect from "../../shared/hooks/useAuthOnCortesiaFromMixtoSelect"
import { RoleNames } from "src/shared/hooks/useAuth"
import { useCurrentDate } from "src/shared/providers/CurrentdateProvider"
import useListaEspera from "./hooks/useListaEspera"
import { DefaultValuesType } from "./VentaHabitacion.types"
import { VentaHabitacionCase } from "./cases/VentaHabitacionCase.types"
import Hospedaje from "./sections/Hospedaje/Hospedaje"
import Huesped from "./sections/Huesped/Huesped"
import Pago from "./sections/Pago/Pago"
import PagoAbonar from "./sections/PagoAbonar/PagoAbonar"
import Propina from "./sections/Propina/Propina"
import PropinaMixto from "./sections/PropinaMixto/PropinaMixto"

// Formulario de venta de habitación compartido por los casos de renta, reserva y venta en línea.
// Lo que cambia entre casos (tarifas, campos bloqueados, sección de pago y venta) lo define `caso`
const VentaHabitacionForm = ({ caso }: { caso: VentaHabitacionCase }) => {
    const { habitacion_id = "" } = useParams()
    const navigate = useNavigate()
    const { hotel_id, usuario_id } = useProfile()
    const { localDateToUTCString, diffDays, setHHMMSS } = useDate()
    const dispatch = useDispatch()

    const { showSnackbar } = useSnackbar()
    const { reserva } = caso

    const [newScheduleTypes, setNewScheduleTypes] = useState<any>(scheduleTypes)

    const [currentDate] = useCurrentDate()

    const { isLoading: isDataLoading, setIsLoading } = useIsLoadingData()

    const { isModalPagoMixtoOpen, tarifas, tarifaSeleccionada } = useSelector((root: RootState) => root.ventaHabitacion)
    const experienciasSeleccionadas = reserva?.experiencias_reserva || []
    const experiencias = experienciasSeleccionadas.map((exp) => ({
        experiencia: { nombre: exp.experiencia?.nombre },
        total: exp.total || 0,
    }))

    const methods = useForm({ defaultValues: ventaHabitacionDefaultValues })

    const tipoHospedaje = methods.watch("type")
    const tarifa_id = methods.watch("amount")
    const extraHours = methods.watch("extraHours")
    const extraHospedajes = methods.watch("extraHospedajes")

    const extraUsers = methods.watch("extraUsers")
    const dateRange = methods.watch("date")
    const costoEarlyCheckIn = methods.watch("costs.costoEarlyCheckIn")
    const costoGeneral = methods.watch("costs.general")
    const isEarlyCheckIn = methods.watch("earlyCheckIn")
    const tipoEntrada = methods.watch("entryType")
    const pagos = useWatch({ control: methods.control, name: "extra" })

    const matricula = methods.watch("matricula")
    const modeloAuto = methods.watch("modelo")
    const marcaAuto = methods.watch("marca")
    const colorAuto = methods.watch("color")

    const nombreHuesped = methods.watch("name")
    const method = methods.watch("method")
    const personas = methods.watch("users")
    const porcentajePropina = methods.watch("porcentajePropina")
    const montoPropina = methods.watch("montoPropina")
    const costos = methods.watch("costs")
    const propinas = methods.watch("propinas")

    const [propinasFormatted, setpropinasFormatted] = useState<DetallePago[]>([])
    const [lovePointsAmount, setLovePointsAmount] = useState<LovePoint | null>(null)
    const [lovePointsAmountGlobal, setLovePointsAmountGlobal] = useState<LovePoint | null>(null)
    const [easyRewardsIdReserva, setEasyRewardsIdReserva] = useState<string | null>(null)
    const { rolName } = useProfile()

    const { isBetween12AMAndCheckInHour, isBetween12AMAndCheckOutHour } = useValidSameDayRent(
        tarifaSeleccionada?.hora_checkin,
        !!reserva,
        tarifaSeleccionada?.hora_checkout
    )
    const [modalMessage, setModalMessage] = useState<string>("")
    const [isModalPagoMixtoLovePointsOpen, setModalPagoMixtoLovePointsOpen] = useState(false)
    const [isModalLovePointsErrorOpen, setModalLovePointsErrorOpen] = useState(false)

    const mostrarErrorLovePoints = (mensaje: string) => {
        setModalMessage(mensaje)
        setModalLovePointsErrorOpen(true)
    }

    const { data: colaboradores } = useGetColaboradoresQuery({
        variables: {
            hotel_id,
        },
    })
    const colaboradoresSuggestions: InputTextSuggestion[] =
        colaboradores?.colaboradores?.map((c) => ({
            title: `${c.nombre} ${c.apellido_paterno} ${c.apellido_materno}`,
            id: c.colaborador_id,
        })) || []

    const { data: habitacion } = useGetHabitacionQuery({
        variables: {
            habitacion_id,
            usuario_id,
        },
        onError: () => {
            navigate("/u")
        },
        onCompleted: (data) => {
            setIsLoading({ key: "habitacion", value: false })
            caso.cargarTarifas(data).then((tarifasCaso) => {
                setIsLoading({ key: "tarifa", value: false })
                dispatch(setTarifas(tarifasCaso))
            })
            setEasyRewardsIdReserva(caso.getEasyRewardsId(data))
        },
    })

    const { turnoAtencionId, folioListaEspera, updateListaEspera } = useListaEspera()

    // hook para transformar los pagos de una reserva en pagos del formulario y hacer operaciones
    const { totalReservaPagos } = useReservaPayments({
        reserva: reserva as Reserva,
    })

    // Establece los valores del formulario de acuerdo a los valores de la reserva
    const [setFormState] = useSetFormStateFromReserva({
        setValue: methods.setValue,
    })

    // Establece los costos totales de las horas, personas, hospedaje, hospedajes extras, totales y totales menos pagos previos
    const { updateCosts } = useSetCosts({
        setValue: methods.setValue,
    })

    const { Modal } = useAuthOnTarifaNocheSelect({
        tipoHabitacionIdSelected: habitacion?.habitacion.tipo_habitacion_id || "",
        tipoHospedajeSelected: tipoHospedaje,
        setValue: (v) => methods.setValue("type", v),
        disabled: !!reserva,
    })

    const { Modal: ModalAuthCortesia } = useAuthOnCortesiaSelect({
        setValue: (v) => methods.setValue("method", v),
        tipoPago: method as TiposPagos,
    })

    const { Modal: ModalAuthCortesiaFromMixto } = useAuthOnCortesiaFromMixtoSelect({
        setValue: (v) => methods.setValue("extra", v),
        tipoPagoList: pagos,
    })

    const [roomDays, setRoomDays] = useState(1)

    const previousCosts = useRef<{
        extraHours: number
        extraUsers: number
        tarifa?: Tarifa
        totalReservaPagos: number
        roomDays: number
        isEarlyCheckIn: boolean
        totalCostoExperiencias: number
        extraHospedajes: number
    }>({
        extraHours: 0,
        extraUsers: 0,
        tarifa: undefined,
        totalReservaPagos: 0,
        roomDays: 0,
        isEarlyCheckIn: false,
        totalCostoExperiencias: 0,
        extraHospedajes: 0,
    })

    // Reinicia los contadores cuando se modifica la selección del tipo de hospedaje o tarifa
    const { updateResetForm, resetAmount } = useResetForm({ setValue: methods.setValue })

    const {
        isModalMatriculaOpen,
        isModalMatriculaOpenFromState,
        setIsModalMatriculaOpen,
        setIsModalMatriculaOpenFromState,
    } = useModalMatricula({ setValue: methods.setValue, tipoEntrada })

    // mostrar campo de pago si los pagos de reserva son menores a lo que se ha agregado a la renta
    const { isShowingPago, setIsShowingPago } = useShowPago({ isFromReserva: !!reserva })

    // mostrar campo de pago si los pagos de reserva son iguales a lo que se ha agregado a la renta
    const { isShowingPagoReserva, setIsShowingPagoReserva } = useShowPagoReserva({
        isFromReserva: !!reserva,
    })

    const seccionPago = caso.getSeccionPago({ isShowingPago, isShowingPagoReserva })

    const { validationByDate } = useTarifasValidation()

    // Reinicia los pagos (excepto los previos que vienen de una reserva) al realizar cambios que afecten a estos para evitar incongruencias
    const { resetPayment } = useResetPayments({ setValue: methods.setValue, costoGeneral: costoGeneral })

    const [isVentaLoading, setIsVentaLoading] = useState(false)

    useEffect(() => {
        if (methods?.formState?.errors?.date) {
            showSnackbar({
                status: "error",
                title: "Error al vender habitación",
                text: "¡Ups! La fecha seleccionada no es válida. Por favor, elige una fecha correcta e inténtalo de nuevo.",
            })
        }
    }, [methods.formState])

    useEffect(() => {
        const tipoAlojamiento = determineTipoAlojamiento()

        const updatedScheduleTypes = scheduleTypes.map((item) => ({
            ...item,
            disabled:
                (tipoAlojamiento === "motel" && item.value === "hotel") ||
                (tipoAlojamiento === "hotel" && item.value === "motel"),
        }))
        setNewScheduleTypes(updatedScheduleTypes)
        setFormState({ reserva })
        setTarifaPorDefault()

        if (tipoAlojamiento === "hotel") {
            methods.setValue("type", TiposAlojamientos.Hotel)
        } else {
            methods.setValue("type", TiposAlojamientos.Motel)
        }
    }, [tarifas])

    useEffect(() => {
        if (easyRewardsIdReserva !== null && lovePointsAmount?.id !== easyRewardsIdReserva && reserva) {
            setLovePointsAmount({
                id: easyRewardsIdReserva,
                saldo: lovePointsAmount?.saldo || 0,
            })
        }
    }, [easyRewardsIdReserva, lovePointsAmount])

    useEffect(() => {
        setLovePointsAmount(null)
        setLovePointsAmountGlobal(null)
    }, [methods.getValues("method")])

    // Establecer la lista de propinas formateadas
    useEffect(() => {
        const propinasList = getPropinaList(
            pagos.map((e) => ({
                subtotal: e.amount,
                tipo_pago: e.type,
                ultimos_digitos: e.number || "",
                easyrewards_id: "",
            })) || [],
            propinas?.length ? propinas : [{ id: "0", value: Number(montoPropina) || 0 }]
        )
        setpropinasFormatted(propinasList)
    }, [JSON.stringify(propinas), pagos, costos.general, montoPropina])

    // Establecer el monto de propina de un pago único al cambiar el % de la propina con los botones
    useEffect(() => {
        if (porcentajePropina === "Otro") {
            methods.setValue("montoPropina", 0)
            return
        }
        methods.setValue("montoPropina", times(costos.general, Number(porcentajePropina)))
    }, [porcentajePropina])

    // Resetear todo lo relacionado a propinas y consumo interno al cambiar de metodo de pago
    useEffect(() => {
        methods.setValue("propinas", ventaHabitacionDefaultValues.propinas)
        methods.setValue("montoPropina", ventaHabitacionDefaultValues.montoPropina)
        methods.setValue("porcentajePropina", ventaHabitacionDefaultValues.porcentajePropina)
        methods.setValue("colaboradorRecibioPropina", ventaHabitacionDefaultValues.colaboradorRecibioPropina)
        methods.setValue("colaboradorConsumoInterno", ventaHabitacionDefaultValues.colaboradorConsumoInterno)
    }, [method])

    useEffect(() => {
        if (colaboradores) {
            return setIsLoading({ key: "colaboradores", value: false })
        }
    }, [colaboradores])

    useEffect(() => {
        dispatch(selectTarifa(tarifas.find((t) => t.tarifa_id === tarifa_id)))
    }, [tarifa_id, tipoHospedaje, tarifas, costoEarlyCheckIn])

    useEffect(() => {
        methods.setValue("earlyCheckIn", false)
        if (!dateRange.length) {
            return setRoomDays(1)
        }
        if (tipoHospedaje === "motel") {
            return setRoomDays(1)
        }
        const diasEstancia = diffDays(addCurrentTime(dateRange?.[0]) || new Date(), addCurrentTime(dateRange?.[1]))

        if (isNaN(diasEstancia)) {
            return setRoomDays(1)
        }
        setRoomDays(diasEstancia < 1 ? 1 : diasEstancia)
    }, [dateRange])

    useEffect(() => {
        const totalCostoExperiencias = experienciasSeleccionadas.reduce((acc, exp) => acc + exp.total, 0)
        const newCosts = {
            extraHours,
            extraUsers: extraUsers + extraUsers * extraHospedajes,
            tarifa: tarifaSeleccionada ?? undefined,
            totalReservaPagos,
            roomDays,
            isEarlyCheckIn,
            totalCostoExperiencias,
            extraHospedajes,
        }

        if (JSON.stringify(newCosts) !== JSON.stringify(previousCosts.current)) {
            updateCosts(newCosts)
            previousCosts.current = newCosts
        }
    }, [
        tarifa_id,
        extraHours,
        extraUsers,
        roomDays,
        tarifaSeleccionada,
        isEarlyCheckIn,
        experienciasSeleccionadas,
        extraHospedajes,
    ])

    useEffect(() => {
        updateResetForm({
            reserva,
            tarifa_id,
            dateRange,
            numero_personas: habitacion?.habitacion.tipo_habitacion?.personas_incluidas,
            isBetween12AMAndCheckOutHour,
        })
    }, [tarifa_id, habitacion, isBetween12AMAndCheckInHour])

    useEffect(() => {
        if (!caso.conservarExtrasDeReserva || tarifa_id !== reserva?.tarifa_id) {
            return
        }
        methods.setValue("extraHours", reserva?.horas_extra || 0)
        methods.setValue("extraHospedajes", reserva?.hospedajes_extra || 0)
    }, [tarifa_id, habitacion, isBetween12AMAndCheckInHour])

    useEffect(() => {
        resetAmount()
        // Reiniciar el día cuando cambia el tipo de hospedaje, esto para que cuando se hayan seleccionado
        if (!reserva) {
            methods.setValue("date", [setHHMMSS({ startDate: new Date(), newHour: "00:00:00", isNewHourInUTC: false })])
        }
        // Seleccionar la primer tarifa por defecto
        setTarifaPorDefault()
    }, [tipoHospedaje])

    useEffect(() => {
        setIsShowingPago({ totalRenta: costos.general })
        setIsShowingPagoReserva({ totalRenta: costos.general })
    }, [costos.general])

    useEffect(() => {
        resetPayment(!isShowingPago)
    }, [tipoHospedaje, tarifa_id, dateRange, extraHours, extraUsers, isEarlyCheckIn, costoGeneral, isShowingPago])

    const setTarifaPorDefault = () => {
        // Se toma el tipo de alojamiento de la tarifa consultada, la que viene en el state puede no traerlo
        const tarifaReserva = tarifas.find((t) => t.tarifa_id === reserva?.tarifa_id)
        methods.setValue(
            "amount",
            reserva && (tarifaReserva?.tipo_alojamiento || reserva?.tarifa?.tipo_alojamiento) === tipoHospedaje
                ? reserva?.tarifa_id
                : tarifas.find(
                      (t) =>
                          t.tipo_habitacion_id === habitacion?.habitacion.tipo_habitacion_id &&
                          t.tipo_alojamiento === tipoHospedaje &&
                          validationByDate(t, dateRange?.[0])
                  )?.tarifa_id || ""
        )
    }

    const determineTipoAlojamiento = () => {
        const tipos = new Set(tarifas.map((tarifa) => tarifa.tipo_alojamiento))
        if (tipos.size === 1) {
            return [...tipos][0]
        } else if (tipos.has("hotel" as TiposAlojamientos) && tipos.has("motel" as TiposAlojamientos)) {
            return "all"
        }
    }

    const handleLovePointsChange = (value) => {
        setLovePointsAmountGlobal(value)
        if (value && value !== null) {
            setLovePointsAmount({
                id: value.id,
                saldo: value.saldo,
            })
        }
    }

    const getCurrentDate = (): Date => currentDate || new Date()

    const onSubmit = async (data: DefaultValuesType) => {
        if (isVentaLoading) {
            return
        }
        setIsVentaLoading(true)
        await caso.onSubmit(data, {
            habitacion,
            tarifaSeleccionada,
            propinasFormatted,
            lovePointsAmount,
            lovePointsAmountGlobal,
            getCurrentDate,
            mostrarErrorLovePoints,
            finalizarTurno: (renta_id) => {
                if (turnoAtencionId) {
                    updateListaEspera(turnoAtencionId, EstadosTurnosAtencion.Finalizado, renta_id)
                }
            },
        })
        setIsVentaLoading(false)
    }
    useEscapeKey({
        onEscape: () => {
            if (isModalMatriculaOpen) {
                setIsModalMatriculaOpen(false)
                methods.setValue("entryType", TiposEntradas.APie)
            }
            if (isModalPagoMixtoOpen) {
                methods.setValue("extra", [])
                methods.setValue("method", "")
                dispatch(toggleModalPagoMixtoOpen(false))
                dispatch(toggleModalPagoMixtoEdited(false))
                return
            }
            navigate("/u")
        },
    })

    return (
        <Screen
            title={`Venta habitación ${habitacion?.habitacion?.tipo_habitacion?.nombre || ""} ${
                habitacion?.habitacion.numero_habitacion || ""
            }${folioListaEspera ? ` - Turno ${folioListaEspera}` : ""} `}
            back
            onBack={async () => {
                if (turnoAtencionId) {
                    await updateListaEspera(turnoAtencionId, EstadosTurnosAtencion.EnEspera)
                    navigate(-1)
                } else {
                    navigate("/u")
                }

                dispatch(toggleRoomDetailsDrawer(false))
            }}
            className="venta-habitacion__container"
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <div className="venta-habitacion__sections">
                        <div className="venta-habitacion__sections__form">
                            <div className="venta-habitacion__header-labels">
                                <LabelTitle
                                    icon="openDoor"
                                    text={`Habitación ${habitacion?.habitacion.tipo_habitacion?.nombre || ""}`}
                                />
                                {habitacion?.habitacion.tipo_habitacion?.camas?.map((c) => (
                                    <LabelTitle
                                        key={uuid()}
                                        icon="BedFilled"
                                        text={`${c.numero || ""} cama(s) ${c.tipo}(es)`}
                                    />
                                ))}
                                <LabelTitle
                                    icon="UserParentFill"
                                    text={`${
                                        habitacion?.habitacion.tipo_habitacion?.personas_incluidas || "-"
                                    } personas`}
                                />
                                <LabelTitle
                                    icon="userAdd"
                                    text={`Máximo ${tarifaSeleccionada?.personas_extra_max || "-"} personas extra`}
                                />
                            </div>
                            <Hospedaje
                                isFromReserva={!!reserva}
                                disabled={isVentaLoading || isDataLoading}
                                scheduleTypes={newScheduleTypes}
                                validarTarifaPorFecha={caso.validarTarifaPorFecha}
                                tipoBloqueado={caso.camposBloqueados.tipo}
                                hospedajesExtraBloqueados={caso.camposBloqueados.hospedajesExtra}
                                earlyCheckInBloqueado={caso.camposBloqueados.earlyCheckIn}
                                isBetween12AMAndCheckInHour={isBetween12AMAndCheckInHour}
                                isBetween12AMAndCheckOutHour={isBetween12AMAndCheckOutHour}
                            />
                            <Huesped
                                isFromReserva={!!reserva}
                                personasIncluidas={habitacion?.habitacion.tipo_habitacion?.personas_incluidas}
                                personasMinimas={reserva?.numero_personas || 1}
                                personasExtraMinimas={reserva?.personas_extras || 0}
                                personasBloqueadas={caso.camposBloqueados.personas}
                                nombreBloqueado={caso.camposBloqueados.nombre}
                                isModalMatriculaOpen={isModalMatriculaOpen}
                                isModalMatriculaOpenFromState={isModalMatriculaOpenFromState}
                                setIsModalMatriculaOpen={setIsModalMatriculaOpen}
                                setIsModalMatriculaOpenFromState={setIsModalMatriculaOpenFromState}
                            />
                            {seccionPago === "pago" && (
                                <Pago
                                    lovePointsAmount={lovePointsAmount}
                                    setLovePointsAmount={setLovePointsAmount}
                                    onLovePointsChange={handleLovePointsChange}
                                    colaboradoresSuggestions={colaboradoresSuggestions}
                                    mostrarErrorLovePoints={mostrarErrorLovePoints}
                                    onConsultarSaldo={() => setModalPagoMixtoLovePointsOpen(true)}
                                />
                            )}
                            {seccionPago === "abonar" && (
                                <PagoAbonar
                                    lovePointsAmount={lovePointsAmount}
                                    setLovePointsAmount={setLovePointsAmount}
                                />
                            )}
                            {rolName !== RoleNames.valet && isValidPropina(method) && !reserva && (
                                <Propina
                                    hasPropinas={!!propinasFormatted.length}
                                    colaboradoresSuggestions={colaboradoresSuggestions}
                                />
                            )}
                            {method === PAYMENT_TYPES.mixto && !!propinasFormatted?.length && !reserva && (
                                <PropinaMixto colaboradoresSuggestions={colaboradoresSuggestions} />
                            )}
                        </div>
                        <Resumen
                            disabled={isVentaLoading || isDataLoading}
                            codigoReserva={caso.codigoReserva}
                            totalPropinas={sum(propinasFormatted.map((p) => p.subtotal))}
                            roomDays={roomDays}
                            lovePointsAmount={lovePointsAmount}
                            hotelId={hotel_id}
                            pagos={[
                                ...pagos.map((pago, index) => {
                                    return {
                                        montoPropina: propinas
                                            ? propinas?.find((p) => Number(p?.id) === index)?.value
                                            : Number(montoPropina) || 0,
                                        amount:
                                            pago.type === PAYMENT_TYPES.LovePoints
                                                ? pago.amount || costos.total
                                                : pago.amount || 0,
                                        type: pago.type,
                                        number: pago.number,
                                        easyrewards_id: lovePointsAmount?.id,
                                    }
                                }),
                                ...(reserva?.pagos?.flatMap(
                                    (p) =>
                                        p.detalles_pago?.flatMap((dp) => ({
                                            amount: dp.subtotal || 0,
                                            type: dp.tipo_pago || "",
                                            number: dp.ultimos_digitos || dp?.numero_referencia || "",
                                            easyrewards_id: lovePointsAmount?.id,
                                        })) || []
                                ) || []),
                            ]}
                            totalAdelantosReserva={sum(reserva?.pagos?.map((p) => p.total) || [])}
                            costs={costos}
                            placas={matricula}
                            modelo={modeloAuto}
                            marca={marcaAuto}
                            color={colorAuto}
                            startDate={dateRange?.[0] ? localDateToUTCString(dateRange?.[0]) : undefined}
                            endDate={dateRange?.[1] ? localDateToUTCString(dateRange?.[1]) : undefined}
                            nombreHuesped={nombreHuesped}
                            personas={personas}
                            personasExtras={extraUsers}
                            personasExtrasMax={tarifaSeleccionada?.personas_extra_max || 0}
                            tipoEntrada={tipoEntrada}
                            tarifa={
                                tarifaSeleccionada
                                    ? `${tarifaSeleccionada?.nombre} - 
                                    ${formatCurrency(tarifaSeleccionada?.costo_habitacion)}`
                                    : ""
                            }
                            tipoTarifa={tipoHospedaje}
                            horasExtras={extraHours}
                            isMixto={method === "mixto"}
                            onSubmit={() => methods.handleSubmit(onSubmit)()}
                            experiencias={experiencias}
                            extraHospedajes={extraHospedajes}
                        />
                    </div>
                </form>
                <LoaderComponent visible={isVentaLoading} />
                {isModalLovePointsErrorOpen && (
                    <ModalLovePointsError
                        isOpen={isModalLovePointsErrorOpen}
                        setIsOpen={setModalLovePointsErrorOpen}
                        description={modalMessage}
                        onCloseDialog={() => setModalLovePointsErrorOpen(false)}
                    />
                )}
                <ModalPagoMixtoLovePoints
                    visible={isModalPagoMixtoLovePointsOpen}
                    onClose={() => setModalPagoMixtoLovePointsOpen(false)}
                    lovePointsAmount={lovePointsAmount}
                    paymentOptions={[
                        PAYMENT_METHODS.efectivo,
                        PAYMENT_METHODS.visaOMasterCard,
                        PAYMENT_METHODS.amex,
                        PAYMENT_METHODS.cortesia,
                        PAYMENT_METHODS.consumoInterno,
                        PAYMENT_METHODS.depositoOTransferencia,
                    ]}
                />
            </FormProvider>
            {Modal}
            {ModalAuthCortesiaFromMixto}
            {ModalAuthCortesia}
        </Screen>
    )
}

export default VentaHabitacionForm
